import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon, Plus, ExternalLink, CheckCircle, AlertCircle, Trash2, Edit3, Users, UserPlus, Mail, Shield, Crown, Eye, Edit, Building2, Building, User, Lock, ChevronDown, ChevronUp, Target, Clock, Award, BookOpen, FolderOpen, CheckSquare, Palette, Bell, Zap, Globe, Link, Tag, Wrench, Archive, CreditCard, Keyboard, Bot, UserCheck, Gavel } from 'lucide-react';
import api from '../utils/api';
import TeamManagement from './TeamManagement';
import NotificationSettings from './NotificationSettings';
import WhiteLabelBrandingSettings from './WhiteLabelBrandingSettings';
import WhiteLabelSetupWizard from './WhiteLabelSetupWizard';
import ClientPortalSettings from './ClientPortalSettings';
import UserProfile from './UserProfile';
import CompanyProfile from './CompanyProfile';
import TaskCategoriesSettings from './TaskCategoriesSettings';
import UserDomainSpecialtiesSettings from './UserDomainSpecialtiesSettings';
import AutoTaskAssignmentSettings from './AutoTaskAssignmentSettings';
import PersonalIntegrations from './PersonalIntegrations';
import ProjectTaskTemplatesSettings from './ProjectTaskTemplatesSettings';
import CompanyNotificationsSettings from './CompanyNotificationsSettings';
import NotificationTemplatesSettings from './NotificationTemplatesSettings';
import CompanyIntegrationsSettings from './CompanyIntegrationsSettings';
import TagManagementSettings from './TagManagementSettings';
import DefaultProjectStagesSettings from './DefaultProjectStagesSettings';
import VariablesSettings from './VariablesSettings';
import ThemeSettings from './ThemeSettings';
import AITaskGenerationSettings from './AITaskGenerationSettings';
import InvitationManager from './InvitationManager';
import RecoverySettings from './RecoverySettings';
import ScopeTemplatesSettings from './ScopeTemplatesSettings';
import HighLevelSyncSettings from './HighLevelSyncSettings';
import WorkHoursSettings from './WorkHoursSettings';
import SecuritySettings from './SecuritySettings';
import ArchiveRules from './ArchiveRules';
import RecurringTaskTemplates from './RecurringTaskTemplates';
import AutomationsSettings from './AutomationsSettings';
import SubscriptionSettings from './SubscriptionSettings';
import KeyboardShortcutsSettings from './KeyboardShortcutsSettings';
import EmailLogs from './EmailLogs';
import TaskAgentSettings from './TaskAgentSettings';
import PermissionsSettings from './PermissionsSettings';
import GovernanceSettings from './GovernanceSettings';
import KnowledgeAccessSettings from './KnowledgeAccessSettings';
import ApiAccessSettings from './ApiAccessSettings';

// Main Settings Component with organized navigation
const Settings = ({ user, currentOrganization, onBack, onShowImportWizard }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('viewer');
  const [teamInfo, setTeamInfo] = useState(null);
  
  // Settings navigation state
  const [activeGroup, setActiveGroup] = useState('personal');
  const [activeSection, setActiveSection] = useState('profile');

  // Settings groups configuration - useMemo ensures re-evaluation when userRole changes
  const settingsGroups = useMemo(() => [
    {
      id: 'personal',
      name: 'Personal',
      icon: User,
      sections: [
        { id: 'profile', name: 'Profile', icon: User, component: 'UserProfile' },
        { id: 'security', name: 'Security', icon: Lock, component: 'SecuritySettings' },
        { id: 'work-hours', name: 'Work Hours', icon: Clock, component: 'WorkHoursSettings' },
        { id: 'appearance', name: 'Appearance', icon: Palette, component: 'ThemeSettings' },
        { id: 'notifications', name: 'Notifications', icon: Bell, component: 'NotificationSettings' },
        { id: 'keyboard-shortcuts', name: 'Keyboard Shortcuts', icon: Keyboard, component: 'KeyboardShortcutsSettings' },
        { id: 'integrations', name: 'Integrations', icon: Zap, component: 'PersonalIntegrations' }
      ]
    },
    {
      id: 'workspace',
      name: 'Workspace', 
      icon: Building2,
      sections: [
        { id: 'automations', name: 'Workflow Automations', icon: Zap, component: 'AutomationsSettings', adminOnly: true },
        { id: 'task-agent', name: 'Task Agent', icon: Bot, component: 'TaskAgentSettings', adminOnly: false },
        { id: 'archive-rules', name: 'Archive Rules', icon: Archive, component: 'ArchiveRules', adminOnly: true },
        { id: 'default-project-stages', name: 'Default Project Stages', icon: FolderOpen, component: 'DefaultProjectStagesSettings', adminOnly: true },
        { id: 'task-categories', name: 'Task Categories', icon: Target, component: 'TaskCategoriesSettings', adminOnly: true },
        { id: 'tag-management', name: 'Tag Management', icon: Tag, component: 'TagManagementSettings', adminOnly: true },
        { id: 'specialties', name: 'Domain Specialties', icon: Award, component: 'UserDomainSpecialtiesSettings', adminOnly: true },
        { id: 'scopes', name: 'Project Scopes', icon: Target, component: 'ScopeTemplatesSettings', adminOnly: true },
        { id: 'auto-assignment', name: 'Auto Assignment', icon: Zap, component: 'AutoTaskAssignmentSettings', adminOnly: true },
        { id: 'company-integrations', name: 'Company Integrations', icon: Globe, component: 'CompanyIntegrationsSettings', adminOnly: true },
        { id: 'highlevel-sync', name: 'HighLevel Task Sync', icon: Link, component: 'HighLevelSyncSettings', adminOnly: true }
      ]
    },
    {
      id: 'templates',
      name: 'Templates',
      icon: BookOpen,
      sections: [
        { id: 'project-task-templates', name: 'Project & Task Templates', icon: FolderOpen, component: 'ProjectTaskTemplatesSettings', adminOnly: true }
      ]
    },
    {
      id: 'team',
      name: 'Team',
      icon: Users,
      sections: [
        { id: 'team-management', name: 'Team Management', icon: Users, component: 'TeamManagement' },
        { id: 'permissions', name: 'Permissions', icon: UserCheck, component: 'PermissionsSettings', adminOnly: true },
        { id: 'knowledge-access', name: 'Knowledge Access', icon: Shield, component: 'KnowledgeAccessSettings', adminOnly: false }
      ]
    },
    {
      id: 'company',
      name: 'Company Settings',
      icon: Building,
      sections: [
        { id: 'company-profile', name: 'Company Profile', icon: Building, component: 'CompanyProfile', adminOnly: true },
        { id: 'governance', name: 'Governance', icon: Gavel, component: 'GovernanceSettings', adminOnly: true },
        { id: 'invitation-manager', name: 'Account Security', icon: Shield, component: 'InvitationManager', adminOnly: true },
        { id: 'ai-task-generation', name: 'AI Task Generation', icon: Zap, component: 'AITaskGenerationSettings', adminOnly: true },
        { id: 'recurring-templates', name: 'Recurring Task Templates', icon: CheckSquare, component: 'RecurringTaskTemplates', adminOnly: true },
        { id: 'company-notifications', name: 'Notification Settings', icon: Bell, component: 'CompanyNotificationsSettings', adminOnly: true },
        { id: 'notification-templates', name: 'Notification Templates', icon: Mail, component: 'NotificationTemplatesSettings', adminOnly: true },
        { id: 'email-logs', name: 'Email Logs', icon: Mail, component: 'EmailLogs', adminOnly: true },
        { id: 'api-access', name: 'API & MCP', icon: Link, component: 'ApiAccessSettings', adminOnly: true },
        { id: 'client-portal', name: 'Client Portal', icon: Globe, component: 'ClientPortalSettings', adminOnly: true },
        { id: 'variables', name: 'Advanced Variables', icon: Target, component: 'VariablesSettings', adminOnly: true },
        { id: 'recovery', name: 'Recovery Center', icon: Shield, component: 'RecoverySettings', adminOnly: true }
      ]
    },
    {
      id: 'branding',
      name: 'Branding',
      icon: Palette,
      sections: [
        { id: 'white-label', name: 'White Label Branding', icon: Palette, component: 'WhiteLabelBrandingSettings', adminOnly: true }
      ]
    },
    {
      id: 'billing',
      name: 'Billing',
      icon: CreditCard,
      sections: [
        { id: 'subscription', name: 'Subscription', icon: CreditCard, component: 'SubscriptionSettings', adminOnly: true }
      ]
    }
  ], [userRole]);

  // Get current section details
  const getCurrentSection = () => {
    const group = settingsGroups.find(g => g.id === activeGroup);
    return group?.sections.find(s => s.id === activeSection);
  };

  useEffect(() => {
    console.log('🔄 Settings useEffect triggered, user:', user ? 'exists' : 'null');
    const fetchUserRole = async () => {
      try {
        const token = localStorage.getItem('access_token');
        console.log('🔑 Token exists:', !!token);
        const response = await api.get('/user/role');
        const role = response.data.role || 'viewer';
        console.log('👤 User role fetched:', role);
        console.log('🔒 Admin access:', ['owner', 'admin'].includes(role));
        setUserRole(role);
        setTeamInfo(response.data.team || null);
      } catch (error) {
        console.error('❌ Failed to fetch user role:', error);
        setUserRole('viewer'); // Default to viewer on error
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      console.log('✅ User exists, calling fetchUserRole...');
      fetchUserRole();
    } else {
      console.log('⚠️ No user prop, setting loading to false');
      setLoading(false);
    }
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="flex-1 flex bg-slate-50">
      {/* Settings Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="p-6">
          <button
            onClick={onBack}
            className="text-purple-600 hover:text-purple-700 font-medium mb-4 transition-colors"
          >
            ← Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Settings</h1>
          <p className="text-sm text-slate-600 mb-6">Manage your workspace</p>

          {/* Settings Navigation */}
          <nav className="space-y-1">
            {settingsGroups
              .filter(group => {
                // Show group if it has at least one accessible section
                const accessibleSections = group.sections.filter(
                  section => !section.adminOnly || ['owner', 'admin'].includes(userRole)
                );
                return accessibleSections.length > 0;
              })
              .map((group) => {
                const accessibleSections = group.sections.filter(
                  section => !section.adminOnly || ['owner', 'admin'].includes(userRole)
                );
                
                return (
                  <div key={group.id}>
                    <div 
                      className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg cursor-pointer transition-colors ${
                        activeGroup === group.id
                          ? 'bg-purple-100 text-purple-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      onClick={() => {
                        setActiveGroup(group.id);
                        setActiveSection(accessibleSections[0].id);
                      }}
                    >
                      <group.icon className="w-4 h-4 mr-3" />
                      {group.name}
                    </div>
                    
                    {activeGroup === group.id && (
                      <div className="ml-4 mt-1 space-y-1">
                        {accessibleSections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => setActiveSection(section.id)}
                        className={`flex items-center w-full px-3 py-2 text-sm rounded-lg transition-colors ${
                          activeSection === section.id
                            ? 'bg-purple-50 text-purple-600 font-medium'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <section.icon className="w-4 h-4 mr-3" />
                        {section.name}
                        {section.adminOnly && (
                          <Shield className="w-3 h-3 ml-auto text-purple-500" />
                        )}
                      </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </nav>
        </div>
      </div>

      {/* Settings Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Content Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-2">
              {getCurrentSection()?.icon && 
                React.createElement(getCurrentSection().icon, { className: "w-6 h-6 text-purple-600" })
              }
              <h2 className="text-2xl font-bold text-slate-900">{getCurrentSection()?.name}</h2>
            </div>
            <p className="text-slate-600">
              {activeGroup === 'personal' && activeSection === 'profile' && 'Manage your personal account information'}
              {activeGroup === 'personal' && activeSection === 'security' && 'Manage your password and account security'}
              {activeGroup === 'personal' && activeSection === 'work-hours' && 'Configure your working hours and availability for scheduling'}
              {activeGroup === 'personal' && activeSection === 'appearance' && 'Customize how Athena looks and feels'}
              {activeGroup === 'personal' && activeSection === 'notifications' && 'Configure your notification preferences'}
              {activeGroup === 'personal' && activeSection === 'keyboard-shortcuts' && 'Customize keyboard shortcuts for faster navigation'}
              {activeGroup === 'personal' && activeSection === 'integrations' && 'Connect your personal accounts and services'}
              {activeGroup === 'workspace' && activeSection === 'task-categories' && 'Organize tasks with custom categories'}
              {activeGroup === 'workspace' && activeSection === 'task-agent' && 'Configure automated task creation from webhooks and emails'}
              {activeGroup === 'workspace' && activeSection === 'tag-management' && 'Manage and organize tags for better task categorization'}
              {activeGroup === 'workspace' && activeSection === 'specialties' && 'Define your domain expertise areas'}
              {activeGroup === 'workspace' && activeSection === 'scopes' && 'Define project scope templates for your workspace'}
              {activeGroup === 'workspace' && activeSection === 'auto-assignment' && 'Configure automatic task assignment rules'}
              {activeGroup === 'workspace' && activeSection === 'company-integrations' && 'Manage company-level integrations and connections'}
              {activeGroup === 'workspace' && activeSection === 'highlevel-sync' && 'Configure two-way task synchronization with GoHighLevel'}
              {activeGroup === 'workspace' && activeSection === 'webhook-management' && 'Configure and manage webhook endpoints for external integrations'}
              {activeGroup === 'templates' && activeSection === 'project-task-templates' && 'Create reusable project and task templates'}
              {activeGroup === 'team' && activeSection === 'team-management' && 'Manage team members and permissions'}
              {activeGroup === 'team' && activeSection === 'permissions' && 'Configure role-based access permissions'}
              {activeGroup === 'team' && activeSection === 'knowledge-access' && 'Control what data users can access through the AI knowledge graph'}
              {activeGroup === 'company' && activeSection === 'company-notifications' && 'Configure basic notification preferences and delivery methods'}
              {activeGroup === 'company' && activeSection === 'notification-templates' && 'Customize email templates and advanced notification settings'}
              {activeGroup === 'company' && activeSection === 'email-logs' && 'View outbound emails sent to clients and team members'}
              {activeGroup === 'company' && activeSection === 'api-access' && 'Connect agents, download the setup workbook, and manage API keys'}
              {activeGroup === 'company' && activeSection === 'client-portal' && 'Configure default client portal settings'}
              {activeGroup === 'company' && activeSection === 'variables' && 'Configure advanced variable templates and automation'}
              {activeGroup === 'company' && activeSection === 'recovery' && 'Recover deleted items within 10 days or permanently remove them'}
              {activeGroup === 'company' && activeSection === 'governance' && 'Enable and configure Board of Directors and Committees features'}
              {activeGroup === 'branding' && activeSection === 'white-label' && 'Customize your brand appearance'}
            </p>
          </div>

          {/* Dynamic Content */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            {activeGroup === 'personal' && activeSection === 'profile' && (
              <UserProfile 
                user={user}
                onClose={null}  // No close needed - inline display
                onUserUpdate={(updatedUser) => {
                  console.log('User updated:', updatedUser);
                  // Trigger a refresh in parent if needed
                  window.location.reload();
                }}
              />
            )}
            {activeGroup === 'personal' && activeSection === 'security' && <SecuritySettings user={user} />}
            {activeGroup === 'personal' && activeSection === 'work-hours' && <WorkHoursSettings user={user} />}
            {activeGroup === 'personal' && activeSection === 'appearance' && <ThemeSettings />}
            {activeGroup === 'personal' && activeSection === 'notifications' && <NotificationSettings user={user} />}
            {activeGroup === 'personal' && activeSection === 'keyboard-shortcuts' && <KeyboardShortcutsSettings />}
            {activeGroup === 'personal' && activeSection === 'integrations' && <PersonalIntegrations user={user} />}
            {activeGroup === 'workspace' && activeSection === 'automations' && <AutomationsSettings user={user} />}
            {activeGroup === 'workspace' && activeSection === 'task-agent' && <TaskAgentSettings user={user} />}
            {activeGroup === 'workspace' && activeSection === 'archive-rules' && <ArchiveRules user={user} />}
            {activeGroup === 'workspace' && activeSection === 'default-project-stages' && <DefaultProjectStagesSettings user={user} />}
            {activeGroup === 'workspace' && activeSection === 'task-categories' && <TaskCategoriesSettings user={user} />}
            {activeGroup === 'workspace' && activeSection === 'tag-management' && <TagManagementSettings user={user} />}
            {activeGroup === 'workspace' && activeSection === 'specialties' && <UserDomainSpecialtiesSettings user={user} />}
            {activeGroup === 'workspace' && activeSection === 'scopes' && <ScopeTemplatesSettings user={user} />}
            {activeGroup === 'workspace' && activeSection === 'auto-assignment' && <AutoTaskAssignmentSettings user={user} />}
            {activeGroup === 'workspace' && activeSection === 'company-integrations' && <CompanyIntegrationsSettings user={user} onShowImportWizard={onShowImportWizard} />}
            {activeGroup === 'workspace' && activeSection === 'highlevel-sync' && <HighLevelSyncSettings />}
            {activeGroup === 'templates' && activeSection === 'project-task-templates' && <ProjectTaskTemplatesSettings user={user} />}
            {activeGroup === 'team' && activeSection === 'team-management' && <TeamManagement user={user} userRole={userRole} teamInfo={teamInfo} />}
            {activeGroup === 'team' && activeSection === 'permissions' && <PermissionsSettings user={user} />}
            {activeGroup === 'team' && activeSection === 'knowledge-access' && <KnowledgeAccessSettings user={user} currentOrganization={currentOrganization} />}
            {activeGroup === 'company' && activeSection === 'company-profile' && <CompanyProfile user={user} />}
            {activeGroup === 'company' && activeSection === 'governance' && <GovernanceSettings user={user} />}
            {activeGroup === 'company' && activeSection === 'invitation-manager' && <InvitationManager user={user} />}
            {activeGroup === 'company' && activeSection === 'ai-task-generation' && <AITaskGenerationSettings user={user} />}
            {activeGroup === 'company' && activeSection === 'recurring-templates' && (
              <RecurringTaskTemplates 
                user={user} 
                onEditTask={(task) => {
                  // Navigate to task URL for consistent behavior
                  navigate(`/tasks/${task.id}`);
                }}
              />
            )}
            {activeGroup === 'company' && activeSection === 'company-notifications' && <CompanyNotificationsSettings user={user} />}
            {activeGroup === 'company' && activeSection === 'notification-templates' && <NotificationTemplatesSettings user={user} />}
            {activeGroup === 'company' && activeSection === 'email-logs' && <EmailLogs />}
            {activeGroup === 'company' && activeSection === 'api-access' && <ApiAccessSettings />}
            {activeGroup === 'company' && activeSection === 'client-portal' && <ClientPortalSettings user={user} currentOrganization={currentOrganization} />}
            {activeGroup === 'company' && activeSection === 'variables' && <VariablesSettings user={user} />}
            {activeGroup === 'company' && activeSection === 'recovery' && <RecoverySettings user={user} />}
            {activeGroup === 'branding' && activeSection === 'white-label' && <WhiteLabelBrandingSettings user={user} />}
            {activeGroup === 'billing' && activeSection === 'subscription' && <SubscriptionSettings user={user} />}
          </div>
        </div>
      </div>

    </div>
  );
};

export default Settings;